import React from 'react'
import { RouteComponentProps } from 'react-router'
import { NodeHtmlMarkdown } from 'node-html-markdown'
import { RemoteImage } from '~/models'
import { bewizrMiscItemsStore } from '~/stores'
import { observer } from '~/ui/component'
import { EmptyOrFetching, Hero, Markdown, Scroller, TextBlock, VBox } from '~/ui/components'
import { AppLayoutConfig } from '~/ui/layouts'
import { createUseStyles, layout } from '~/ui/styling'
import BeWizrEmbedItemView from './BeWizrEmbedItemView'
import BeWizrLinkItemView from './BeWizrLinkItemView'
import BeWizrMediaItemView from './BeWizrMediaItemView'

export interface BeWizrMiscItemScreenParams {
  type: 'embed' | 'link' | 'media'
  id:   string
}

export type Props = RouteComponentProps<BeWizrMiscItemScreenParams>

const BeWizrMiscItemScreen = observer('BeWizrMiscItemScreen', (props: Props) => {

  const {type, id} = props.match.params

  const document    = bewizrMiscItemsStore.items.document(id)
  const item        = document.data
  const fetchStatus = document.fetchStatus

  React.useEffect(() => {
    document.type = type
    document.fetch()
  }, [document, type])

  const imageSource = React.useMemo(() => {
    if (item?.links.image != null) {
      return item.links.image
    } else if (item?.type === 'embeditem') {
      return item.embedJson.thumbnailUrl
    } else if (item?.type === 'linkitem') {
      return item.openGraphJson?.image
    }
  }, [item])

  const image: RemoteImage | undefined = imageSource == null ? undefined : {
    type: 'remote',
    url:   imageSource,
  }

  const description = React.useMemo(() => {
    if (item?.descriptionHtml == null || item.descriptionHtml.length === 0) { return null }
    // A little bit ridiculous, but this way we can use the styling from the <Markdown/> component
    return NodeHtmlMarkdown.translate(item.descriptionHtml)
  }, [item])

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return (
      <>
        <AppLayoutConfig
          title={item?.title}
          fetchStatus={fetchStatus}
        />
        <Scroller>
          {item == null ? renderEmpty() : renderBody()}
        </Scroller>
      </>
    )
  }

  function renderBody() {
    if (item == null) { return null }

    return (
      <Scroller>
        <VBox classNames={$.BeWizrMiscItemScreen}>
          <Hero
            title={item.title}
            image={image}
            backgroundImage={image}
            children={renderHeroContent()}
          />
          <VBox align='center' padding={layout.padding.m}>
            {renderContent()}
          </VBox>
        </VBox>
      </Scroller>
    )
  }

  function renderHeroContent() {
    if (item?.shortDescription == null) { return null }

    return (
      <TextBlock>
        {item.shortDescription}
      </TextBlock>
    )
  }

  function renderContent() {
    if (item == null) { return null}
    return (
      <VBox classNames={$.content} gap={layout.padding.xl} paddingVertical={layout.padding.l}>
        {renderDescription()}
        {item.type === 'embeditem' && <BeWizrEmbedItemView item={item}/>}
        {item.type === 'linkitem'  && <BeWizrLinkItemView item={item}/>}
        {item.type === 'mediaitem' && <BeWizrMediaItemView item={item}/>}
      </VBox>
    )
  }

  function renderDescription() {
    if (item == null || description == null) { return null }

    return (
      <Markdown>
        {description}
      </Markdown>
    )
  }


  function renderEmpty() {
    return (
      <EmptyOrFetching
        flex
        status={fetchStatus}
      />
    )
  }

  return render()

})

export default BeWizrMiscItemScreen

const useStyles = createUseStyles({
  BeWizrMiscItemScreen: {},
  content: {
    width:    layout.contentWidth,
    maxWidth: '100%',
  },
})